import {
  Button,
  TableBodyContainer,
  TableBodyLine,
  TableContainer,
  TableHeader,
  Input,
  Text1,
} from '@touche-lts/panel-ui/dist'
import React, { useEffect, useState } from 'react'
import { Api } from 'utilities'
import styles from './allowed-sso-emails-table.module.scss'

export interface AllowedSsoEmailItem {
  id: number
  email: string
}

const AllowedSsoEmailsPageComponent: React.FC = () => {
  const [list, setList] = useState<AllowedSsoEmailItem[]>([])
  const [loading, setLoading] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [error, setError] = useState<string | null>(null)

  const loadAllowedEmails = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await Api.get<AllowedSsoEmailItem[]>('/sso/allowed-emails', 'userApi')
      setList(Array.isArray(data) ? data : [])
    } catch (e) {
      const err = e?.message ? JSON.parse(e.message) : {}
      setError(err?.message || 'Failed to load allowed emails')
      setList([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAllowedEmails()
  }, [])

  const handleAdd = async () => {
    const trimmed = newEmail.trim()
    if (!trimmed) return
    setLoading(true)
    setError(null)
    try {
      await Api.post('/sso/allowed-emails', { email: trimmed }, 'userApi')
      setNewEmail('')
      await loadAllowedEmails()
    } catch (e) {
      const err = e?.message ? JSON.parse(e.message) : {}
      setError(err?.message || 'Failed to add email')
    } finally {
      setLoading(false)
    }
  }

  const handleRemove = async (email: string) => {
    setLoading(true)
    setError(null)
    try {
      await Api.delete(
        `/sso/allowed-emails?email=${encodeURIComponent(email)}`,
        'userApi'
      )
      await loadAllowedEmails()
    } catch (e) {
      const err = e?.message ? JSON.parse(e.message) : {}
      setError(err?.message || 'Failed to remove email')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.section}>
      <h3>Allowed SSO Emails</h3>
      <p className={styles.description}>
        Only these email addresses can sign in to the backoffice via SSO (Google/Microsoft).
        Add emails below to allow new users to register or sign in with SSO.
      </p>
      {error && (
        <div className={styles.error}>
          <Text1>{error}</Text1>
        </div>
      )}
      <div className={styles.addRow}>
        <Input
          type="email"
          placeholder="Enter email address"
          value={newEmail}
          onChange={(value) => setNewEmail(value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
        <Button preset="primary" onClick={handleAdd} disabled={loading || !newEmail.trim()}>
          Add email
        </Button>
      </div>
      <TableContainer className={styles.tableWrapper}>
        <TableHeader
          tableColumns={[
            { label: 'Email', className: styles.emailColumn },
            { label: 'Actions', className: styles.actionsColumn },
          ]}
        />
        <TableBodyContainer className={styles.tableWrapper}>
          {list.length === 0 && !loading && (
            <TableBodyLine
              className={styles.tableWrapper}
              cells={[
                {
                  children: <Text1>No allowed SSO emails yet. Add one above.</Text1>,
                  className: styles.emailColumn,
                },
                { children: null, className: styles.actionsColumn },
              ]}
            />
          )}
          {list.map((item) => (
            <TableBodyLine
              key={item.id}
              className={styles.tableWrapper}
              cells={[
                {
                  children: <Text1>{item.email}</Text1>,
                  className: styles.emailColumn,
                },
                {
                  children: (
                    <Button
                      preset="secondary"
                      onClick={() => handleRemove(item.email)}
                      disabled={loading}
                    >
                      Remove
                    </Button>
                  ),
                  className: styles.actionsColumn,
                },
              ]}
            />
          ))}
        </TableBodyContainer>
      </TableContainer>
    </div>
  )
}

export const AllowedSsoEmailsPage = AllowedSsoEmailsPageComponent
